import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationConfig } from '@/types/list.types';
import { calculateTotalPages, isFirstPage, isLastPage } from '@/utils/list.utils';
import { PAGE_SIZE_OPTIONS } from '@/constants/list.constants';

interface ListPaginationProps {
  pagination: PaginationConfig;
  total: number;
  onChange: (config: PaginationConfig) => void;
  pageSizeOptions?: number[];
  showPageSize?: boolean;
}

export function ListPagination({
  pagination,
  total,
  onChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  showPageSize = true,
}: ListPaginationProps) {
  const { page, limit } = pagination;
  const totalPages = calculateTotalPages(total, limit);

  const handlePreviousPage = () => {
    if (!isFirstPage(page)) {
      onChange({ ...pagination, page: page - 1 });
    }
  };

  const handleNextPage = () => {
    if (!isLastPage(page, total, limit)) {
      onChange({ ...pagination, page: page + 1 });
    }
  };

  const handleGoToPage = (p: number) => {
    onChange({ ...pagination, page: p });
  };

  const handleLimitChange = (newLimit: number) => {
    const currentFirstItem = (page - 1) * limit;
    const newPage = Math.floor(currentFirstItem / newLimit) + 1;
    onChange({ page: newPage, limit: newLimit });
  };

  // Build page numbers with ellipsis
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [1];

    if (page > 3) {
      pages.push('ellipsis');
    }

    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (page < totalPages - 2) {
      pages.push('ellipsis');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [page, totalPages]);

  if (totalPages <= 1 && !showPageSize) {
    return null;
  }

  const firstItem = (page - 1) * limit + 1;
  const lastItem = Math.min(page * limit, total);

  return (
    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3 tw-mt-6 tw-border-t tw-border-gray-200 tw-pt-4">
      {/* Left: count + page size */}
      <div className="tw-flex tw-items-center tw-gap-4">
        <span className="tw-text-sm tw-text-gray-500">
          {firstItem}–{lastItem} sur {total}
        </span>
        {showPageSize && (
          <select
            value={limit}
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            className="tw-border tw-border-gray-200 tw-rounded-lg tw-px-2 tw-py-1 tw-text-sm tw-text-gray-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option} / page
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Right: page navigation */}
      {totalPages > 1 && (
        <div className="tw-flex tw-items-center tw-gap-1">
          {/* Previous */}
          <button
            onClick={handlePreviousPage}
            disabled={isFirstPage(page)}
            className="tw-p-1.5 tw-rounded-lg tw-text-gray-500 hover:tw-bg-gray-100 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed tw-transition-colors"
            title="Précédent"
          >
            <ChevronLeft className="tw-w-4 tw-h-4" />
          </button>

          {/* Page numbers (desktop) */}
          <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-1">
            {pageNumbers.map((p, idx) =>
              p === 'ellipsis' ? (
                <span key={`e-${idx}`} className="tw-px-1 tw-text-sm tw-text-gray-400">
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => handleGoToPage(p)}
                  className={`tw-min-w-[32px] tw-h-8 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors ${
                    p === page
                      ? 'tw-bg-affinicar-primary tw-text-white'
                      : 'tw-text-gray-600 hover:tw-bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              )
            )}
          </div>

          {/* Mobile: simple indicator */}
          <span className="sm:tw-hidden tw-text-sm tw-text-gray-500 tw-px-2">
            {page}/{totalPages}
          </span>

          {/* Next */}
          <button
            onClick={handleNextPage}
            disabled={isLastPage(page, total, limit)}
            className="tw-p-1.5 tw-rounded-lg tw-text-gray-500 hover:tw-bg-gray-100 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed tw-transition-colors"
            title="Suivant"
          >
            <ChevronRight className="tw-w-4 tw-h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
